import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, DollarSign, Check, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const GOLD  = "#F5C842";
const DOC_SLOTS = [
  { key: "mvr",           label: "MVR",             desc: "Motor Vehicle Record" },
  { key: "psp",           label: "PSP",             desc: "Pre-Employment Screening Program" },
  { key: "clearinghouse", label: "Clearinghouse",   desc: "Drug & Alcohol Clearinghouse" },
];

export function CarrierDocUploadModal({ bid, token, onClose, onDone }) {
  const [uploads, setUploads]   = useState({});   // { docType: "uploading"|"done"|"error" }
  const [credited, setCredited] = useState(false);
  const refs = { mvr: useRef(), psp: useRef(), clearinghouse: useRef() };

  async function handleFile(docType, file) {
    if (!file) return;
    setUploads(u => ({ ...u, [docType]: "uploading" }));
    try {
      const res = await api.marketCarrierUploadDoc(token, bid.driver_id, docType, file);
      setUploads(u => ({ ...u, [docType]: "done" }));
      if (res.credit_pending) {
        setCredited(true);
        toast.success("$30 credit earned — activates if you don't hire this driver.");
      } else {
        toast.success(`${docType.toUpperCase()} uploaded.`);
      }
    } catch (err) {
      setUploads(u => ({ ...u, [docType]: "error" }));
      toast.error("Upload failed — try again.");
    }
  }

  const anyDone = Object.values(uploads).includes("done");

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(12px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.22 }}
          className="relative w-full max-w-md"
          style={{ background: "#0e0e12", border: "1px solid rgba(245,200,66,0.28)", boxShadow: "0 0 60px rgba(245,200,66,0.1)" }}
        >
          <div style={{ height: 3, background: `linear-gradient(90deg,${GOLD},#d97706)` }} />
          <button onClick={onClose} className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>

          <div className="px-6 pt-5 pb-2">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4" style={{ color: GOLD }} />
              <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/40">Earn $30 Credit</p>
            </div>
            <h2 className="font-head font-black text-xl uppercase tracking-[0.04em] text-white mb-1">
              Upload Docs for {bid.driver_name}
            </h2>
            <p className="text-xs text-white/40 leading-relaxed mb-4">
              Upload any or all of the docs below. You'll earn one <span style={{ color: GOLD }} className="font-bold">$30 credit</span> for this driver — it activates automatically if someone else hires them.
            </p>

            {/* Credit earned badge */}
            {credited && (
              <div className="flex items-center gap-2 px-3 py-2.5 mb-4"
                style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)" }}>
                <Check className="w-3.5 h-3.5 shrink-0" style={{ color: "#22C55E" }} />
                <p className="text-xs font-head font-bold" style={{ color: "#22C55E" }}>
                  $30 pending credit earned — activates when this driver goes to another carrier.
                </p>
              </div>
            )}
          </div>

          <div className="px-6 pb-3 space-y-2">
            {DOC_SLOTS.map(({ key, label, desc }) => {
              const st = uploads[key];
              return (
                <div key={key}
                  className="flex items-center gap-3 px-4 py-3"
                  style={{
                    background: st === "done" ? "rgba(34,197,94,0.06)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${st === "done" ? "rgba(34,197,94,0.25)" : st === "error" ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.07)"}`,
                  }}>
                  <FileText className="w-3.5 h-3.5 shrink-0 text-white/25" />
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-xs font-bold uppercase tracking-[0.1em] text-white">{label}</p>
                    <p className="text-[10px] text-white/30">{desc}</p>
                  </div>
                  <input
                    ref={refs[key]}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                    className="hidden"
                    onChange={(e) => { handleFile(key, e.target.files?.[0]); e.target.value = ""; }}
                  />
                  {st === "done" ? (
                    <span className="font-head text-[9px] font-black uppercase px-2 py-1"
                      style={{ background: "rgba(34,197,94,0.12)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.3)" }}>
                      <Check className="w-3 h-3 inline mr-1" />Uploaded
                    </span>
                  ) : st === "uploading" ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                  ) : (
                    <button
                      data-testid={`carrier-doc-upload-${key}`}
                      onClick={() => refs[key].current?.click()}
                      className="font-head text-[9px] font-black uppercase px-3 py-1.5 border transition-all"
                      style={{ borderColor: "rgba(245,200,66,0.3)", color: GOLD, background: "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(245,200,66,0.08)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      <Upload className="w-3 h-3 inline mr-1" />Upload
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-6 pb-5 pt-2 flex gap-2">
            {anyDone ? (
              <button
                data-testid="carrier-doc-upload-done-btn"
                onClick={onDone}
                className="flex-1 py-3 font-head text-sm font-black uppercase tracking-[0.2em] transition-all hover:brightness-110"
                style={{ background: `linear-gradient(90deg,${GOLD},#d97706)`, color: "white" }}>
                Done — View Credits Tab
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex-1 py-2.5 font-head text-sm uppercase tracking-[0.18em] border transition-all"
                style={{ borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>
                Cancel
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
